import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  Loader2,
  Search,
  HelpCircle,
  UserPlus,
  Pill,
  Activity,
  AlertCircle,
  FolderHeart,
  ShoppingBag,
  Users,
  ChevronDown,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { api } from "@/lib/api";
import Footer from "@/components/Footer";

const CATEGORY_ORDER = [
  "Account & Getting Started",
  "Medications & Reminders",
  "Health Tracking & Reports",
  "Emergency & SOS Features",
  "Digital Health Records",
  "Services & Shopping",
  "Caregiver Support",
];

const CATEGORY_ICONS = {
  "Account & Getting Started": UserPlus,
  "Medications & Reminders": Pill,
  "Health Tracking & Reports": Activity,
  "Emergency & SOS Features": AlertCircle,
  "Digital Health Records": FolderHeart,
  "Services & Shopping": ShoppingBag,
  "Caregiver Support": Users,
};

function renderAnswer(text) {
  // Lightweight inline markdown: paragraphs, **bold**, numbered lists.
  const paragraphs = (text || "").split(/\n\s*\n/);
  return paragraphs.map((p, i) => {
    const lines = p.split("\n").map((l) => l.trim()).filter(Boolean);
    const isOrdered = lines.length > 1 && lines.every((l) => /^\d+\./.test(l));
    if (isOrdered) {
      return (
        <ol
          key={i}
          className="list-decimal pl-5 space-y-1.5 text-[15px] leading-relaxed text-[#3D5560]"
        >
          {lines.map((l, k) => (
            <li key={k}>{renderInline(l.replace(/^\d+\.\s*/, ""))}</li>
          ))}
        </ol>
      );
    }
    return (
      <p
        key={i}
        className="text-[15px] leading-relaxed text-[#3D5560] whitespace-pre-line"
      >
        {renderInline(p)}
      </p>
    );
  });
}

function renderInline(text) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, idx) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return (
        <strong key={idx} className="font-semibold text-[#1F4E5F]">
          {part.slice(2, -2)}
        </strong>
      );
    }
    return <span key={idx}>{part}</span>;
  });
}

export default function FAQ() {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");

  useEffect(() => {
    document.title = "FAQ · Careable 360+ Plus";
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/faqs");
        if (!cancelled) setFaqs(data || []);
      } catch (err) {
        if (!cancelled)
          setError(err?.response?.data?.detail || err.message || "Failed to load FAQs");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const grouped = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = q
      ? faqs.filter(
          (f) =>
            f.question.toLowerCase().includes(q) ||
            f.answer.toLowerCase().includes(q) ||
            f.category.toLowerCase().includes(q),
        )
      : faqs;
    const map = new Map();
    for (const f of filtered) {
      const arr = map.get(f.category) || [];
      arr.push(f);
      map.set(f.category, arr);
    }
    for (const [k, arr] of map) {
      arr.sort(
        (a, b) =>
          (a.sort_order ?? 0) - (b.sort_order ?? 0) ||
          a.question.localeCompare(b.question),
      );
      map.set(k, arr);
    }
    const sortedCategories = Array.from(map.keys()).sort((a, b) => {
      const ai = CATEGORY_ORDER.indexOf(a);
      const bi = CATEGORY_ORDER.indexOf(b);
      if (ai === -1 && bi === -1) return a.localeCompare(b);
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    });
    return sortedCategories.map((cat) => ({ category: cat, items: map.get(cat) }));
  }, [faqs, query]);

  return (
    <div
      data-testid="faq-page"
      className="min-h-screen bg-[#F4FAF8] text-[#0E2A36] flex flex-col"
    >
      {/* Hero */}
      <header className="bg-[#1F4E5F] text-[#F4FAF8]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
          <Link
            data-testid="faq-back-home"
            to="/"
            className="inline-flex items-center gap-1.5 text-sm text-[#F4FAF8]/75 hover:text-[#F4FAF8] mb-6 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Back to home
          </Link>
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-[#E8A33D] mb-3">
            <HelpCircle className="w-3.5 h-3.5" /> Help Center
          </div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold leading-tight max-w-3xl">
            Frequently asked questions
          </h1>
          <p className="mt-4 text-[#F4FAF8]/80 text-base sm:text-lg max-w-2xl leading-relaxed">
            Everything you need to know about getting started, medications, vitals, SOS,
            records, and caregivers in Careable 360+ Plus.
          </p>

          {/* Search */}
          <div className="mt-7 max-w-xl relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#1F4E5F]/60" />
            <input
              data-testid="faq-search-input"
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search questions, e.g. SOS, medications, language..."
              className="w-full pl-11 pr-4 py-3 rounded-full bg-[#F4FAF8] text-[#0E2A36] placeholder:text-[#3D5560]/55 border border-transparent focus:outline-none focus:ring-2 focus:ring-[#E8A33D]/60 shadow-sm"
            />
          </div>
        </div>
      </header>

      {/* Body */}
      <main className="flex-1 max-w-6xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16">
        {loading && (
          <div
            data-testid="faq-loading"
            className="flex items-center gap-2 text-[#3D5560]"
          >
            <Loader2 className="w-4 h-4 animate-spin" /> Loading questions…
          </div>
        )}

        {!loading && error && (
          <div
            data-testid="faq-error"
            className="rounded-2xl border border-rose-200 bg-rose-50 text-rose-700 px-5 py-4 text-sm"
          >
            {error}
          </div>
        )}

        {!loading && !error && grouped.length === 0 && (
          <div
            data-testid="faq-empty"
            className="rounded-2xl border border-[#C9DDD6] bg-white px-6 py-10 text-center"
          >
            <p className="text-[#3D5560]">
              No questions match{" "}
              <span className="font-semibold text-[#1F4E5F]">“{query}”</span>.
            </p>
            <button
              data-testid="faq-clear-search"
              onClick={() => setQuery("")}
              className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-[#1F4E5F] hover:text-[#173E50]"
            >
              Clear search
            </button>
          </div>
        )}

        <div className="space-y-10">
          {grouped.map(({ category, items }, idx) => {
            const Icon = CATEGORY_ICONS[category] || HelpCircle;
            return (
              <motion.section
                key={category}
                data-testid={`faq-category-${idx}`}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.35, delay: idx * 0.04 }}
                className="bg-white rounded-3xl border border-[#C9DDD6] shadow-[0_4px_20px_rgba(15,56,32,0.04)] overflow-hidden"
              >
                <div className="px-6 sm:px-8 py-5 flex items-center gap-3 border-b border-[#E1EEEA] bg-[#F4FAF8]/60">
                  <div className="w-10 h-10 rounded-2xl bg-[#E1EEEA] flex items-center justify-center text-[#1F4E5F]">
                    <Icon className="w-5 h-5" />
                  </div>
                  <div>
                    <h2 className="font-display text-lg sm:text-xl font-bold text-[#1F4E5F]">
                      {category}
                    </h2>
                    <p className="text-xs text-[#3D5560]/80">
                      {items.length} {items.length === 1 ? "question" : "questions"}
                    </p>
                  </div>
                </div>
                <Accordion
                  type="single"
                  collapsible
                  className="px-3 sm:px-5 py-2"
                >
                  {items.map((faq) => (
                    <AccordionItem
                      key={faq.id}
                      value={faq.id}
                      className="border-b border-[#E1EEEA] last:border-b-0"
                    >
                      <AccordionTrigger
                        data-testid={`faq-item-${faq.id}`}
                        className="px-4 sm:px-5 py-4 text-left text-[15px] sm:text-base font-semibold text-[#0E2A36] hover:no-underline hover:text-[#1F4E5F] [&[data-state=open]]:text-[#1F4E5F]"
                      >
                        {faq.question}
                      </AccordionTrigger>
                      <AccordionContent className="px-4 sm:px-5 pb-5">
                        <div className="space-y-3">{renderAnswer(faq.answer)}</div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </motion.section>
            );
          })}
        </div>

        {/* Still need help CTA */}
        {!loading && !error && (
          <div
            data-testid="faq-cta"
            className="mt-14 rounded-3xl bg-gradient-to-br from-[#1F4E5F] to-[#2B6677] text-[#F4FAF8] px-6 sm:px-10 py-10 sm:py-12"
          >
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#E8A33D]">
                  Still have questions?
                </p>
                <h3 className="font-display text-2xl sm:text-3xl font-bold mt-2 max-w-xl">
                  Talk to a real person — your Health Coordinator is just a call away.
                </h3>
              </div>
              <Link
                data-testid="faq-cta-contact"
                to="/#contact"
                className="inline-flex items-center justify-center gap-2 bg-[#E8A33D] hover:bg-[#C88A28] text-white px-6 py-3 rounded-full text-sm font-semibold transition-all hover:gap-3"
              >
                Contact us <ChevronDown className="w-4 h-4 rotate-[-90deg]" />
              </Link>
            </div>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}
